import React, { useState } from "react";
import {
  Button,
  Container,
  TextField,
  Typography,
  Box,
  Link,
  Paper,
} from "@mui/material";
import { auth } from "../../api/fbConfig";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";

const AuthPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [authMode, setAuthMode] = useState("login"); // 'login', 'register', 'forgot'

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      window.location.href = "/";
    } catch (err) {
      setError("Failed to log in with Google. Please try again.");
      console.error("Google login error:", err);
    }
  };

  const handleAuthAction = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (authMode === "login") {
      try {
        await signInWithEmailAndPassword(auth, email, password);
        window.location.href = "/";
      } catch (err) {
        setError("Incorrect email or password. Please try again.");
        console.error("Login error:", err);
      }
    } else if (authMode === "register") {
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }

      try {
        await createUserWithEmailAndPassword(auth, email, password);
        setSuccessMessage("Account created successfully! You can now log in.");
        setAuthMode("login");
      } catch (err) {
        setError("Failed to create account. Please try again.");
        console.error("Registration error:", err);
      }
    } else if (authMode === "forgot") {
      try {
        await sendPasswordResetEmail(auth, email);
        setSuccessMessage("Password reset email sent. Check your inbox.");
        setAuthMode("login");
      } catch (err) {
        setError("Failed to send password reset email. Please try again.");
        console.error("Password reset error:", err);
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5, textAlign: "center" }}>
      {/* Logo */}
      <Box sx={{ mb: 3 }}>
        <img
          src="/images/logo/logo.png"
          alt="Logo"
          style={{
            width: "80%",
            maxWidth: "200px",
            marginBottom: "10px",
          }}
        />
      </Box>

      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h4" gutterBottom>
          {authMode === "login"
            ? "Login"
            : authMode === "register"
            ? "Create Account"
            : "Reset Password"}
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {successMessage && (
          <Typography color="success" sx={{ mb: 2 }}>
            {successMessage}
          </Typography>
        )}

        <form onSubmit={handleAuthAction} style={{ marginBottom: "20px" }}>
          {/* Email Field (always visible) */}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            margin="normal"
          />

          {/* Password Field (only for login & register) */}
          {authMode !== "forgot" && (
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
          )}

          {/* Confirm Password Field (only for register) */}
          {authMode === "register" && (
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
          )}

          {/* Submit Button */}
          <Button type="submit" variant="contained" color="primary" fullWidth>
            {authMode === "login"
              ? "Login"
              : authMode === "register"
              ? "Register"
              : "Reset Password"}
          </Button>
        </form>

        {/* Google Login Button */}
        {authMode !== "forgot" && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleGoogleLogin}
            fullWidth
            sx={{ mt: 1 }}
          >
            Login with Google
          </Button>
        )}

        {/* Navigation Links */}
        <Box sx={{ mt: 2 }}>
          {authMode === "login" && (
            <>
              <Link
                component="button"
                variant="body2"
                onClick={() => setAuthMode("forgot")}
                sx={{ cursor: "pointer", display: "block", mb: 1 }}
              >
                Forgot Password?
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={() => setAuthMode("register")}
                sx={{ cursor: "pointer" }}
              >
                Create a New Account
              </Link>
            </>
          )}

          {authMode === "register" && (
            <Link
              component="button"
              variant="body2"
              onClick={() => setAuthMode("login")}
              sx={{ cursor: "pointer" }}
            >
              Already have an account? Log in here.
            </Link>
          )}

          {authMode === "forgot" && (
            <Link
              component="button"
              variant="body2"
              onClick={() => setAuthMode("login")}
              sx={{ cursor: "pointer" }}
            >
              Remembered your password? Log in here.
            </Link>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default AuthPage;
